import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GraduationCap, Eye, EyeSlash, Spinner } from '@phosphor-icons/react';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/my-learning';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      let redirectTo = from;
      if (result.user.role === 'admin') redirectTo = '/admin';
      else if (result.user.role === 'client_admin') redirectTo = '/company';
      navigate(redirectTo, { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-[#2C3E50]/10 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#2C3E50] flex items-center justify-center mx-auto mb-4">
                <GraduationCap size={32} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold text-[#1A252F]">Bem-vindo de Volta</h1>
              <p className="text-[#5C6B7A] mt-2">Entre para continuar aprendendo</p>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-lg bg-[#E74C3C]/10 border border-[#E74C3C]/20" data-testid="login-error">
                <p className="text-sm text-[#E74C3C]">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-[#1A252F] font-medium">
                  Login
                </Label>
                <Input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome.sobrenome ou email@exemplo.com"
                  className="mt-2 bg-white border-[#BDC3C7] focus:border-[#2C3E50] focus:ring-[#2C3E50]"
                  required
                  data-testid="login-email"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-[#1A252F] font-medium">
                  Senha
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Digite sua senha"
                    className="bg-white border-[#BDC3C7] focus:border-[#2C3E50] focus:ring-[#2C3E50] pr-10"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B7A] hover:text-[#2C3E50]"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2C3E50] text-white hover:bg-[#1A252F] py-6 font-medium"
                data-testid="login-submit"
              >
                {loading ? (
                  <Spinner size={20} className="animate-spin" />
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-[#5C6B7A]">
                Não tem uma conta?{' '}
                <Link 
                  to="/register" 
                  className="text-[#27AE60] hover:underline font-medium"
                  data-testid="register-link"
                >
                  Cadastre-se aqui
                </Link>
              </p>
            </div>

            {/* Demo credentials removed for production */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
